import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useRealtimeSubscription } from '../lib/realtime';
import { canonicalMoney, sumMoney } from '../lib/money';
import { isNeedsReview } from '../lib/t2125Mapping';
import type { Expense, ExpenseFilters } from '../types';

function formatDate(d: Date): string { return d.toISOString().split('T')[0]; }

function getFilterDateStart(range: ExpenseFilters['dateRange']): string | null {
  if (range === 'all') return null;
  const d = new Date();
  if (range === 'month') d.setMonth(d.getMonth() - 1);
  else if (range === 'quarter') d.setMonth(d.getMonth() - 3);
  else d.setFullYear(d.getFullYear() - 1);
  return formatDate(d);
}

export interface ExpenseSummaryData {
  thisMonth: number;
  ytdDeductibles: number;
  missingReceipts: number;
}

interface UseExpensesResult {
  expenses: Expense[];
  summary: ExpenseSummaryData;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  filters: ExpenseFilters;
  setFilters: (f: ExpenseFilters) => void;
}

export function useExpenses(businessId: string | undefined): UseExpensesResult {
  const [allExpenses, setAllExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ExpenseFilters>({ category: null, dateRange: 'all', search: '' });

  // Always fetch the full set (including archived); filter client-side. Lets the
  // show-archived toggle flip without a re-fetch and keeps the realtime delta
  // logic simple. Per Session 129 ExpensesTable / T2125 grouping needs, the
  // select also pulls t2125_line + phase_id + is_hst_exempt + deductible_amount.
  const fetchAll = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const { data, error: err } = await supabase
        .from('expenses')
        .select('id, category, description, amount, tax_amount, date, payment_method, vendor_name, receipt_url, receipt_filename, is_recurring, notes, project_id, job_id, phase_id, trip_id, logged_by, created_at, mileage_km, mileage_rate, mileage_from, mileage_to, ai_categorized, ocr_data, ocr_confidence, deductible_percentage, deductible_amount, is_hst_exempt, t2125_line, archived')
        .eq('business_id', businessId)
        .order('date', { ascending: false });
      if (err) throw err;
      setAllExpenses((data ?? []) as Expense[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load expenses');
    } finally { setLoading(false); }
  }, [businessId]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);

  const rtTables = useMemo(() => ['expenses'], []);
  useRealtimeSubscription(businessId, rtTables, fetchAll);

  const expenses = useMemo(() => {
    let list = allExpenses;
    if (!filters.includeArchived) list = list.filter(e => !e.archived);
    if (filters.category) list = list.filter(e => e.category === filters.category);
    const dateStart = getFilterDateStart(filters.dateRange);
    if (dateStart) list = list.filter(e => e.date >= dateStart);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter(e =>
        e.description.toLowerCase().includes(q) ||
        (e.vendor_name ?? '').toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q)
      );
    }
    if (filters.needsReviewOnly) list = list.filter(isNeedsReview);
    return list;
  }, [allExpenses, filters]);

  const summary = useMemo<ExpenseSummaryData>(() => {
    const now = new Date();
    const monthStart = formatDate(new Date(now.getFullYear(), now.getMonth(), 1));
    const yearStart = formatDate(new Date(now.getFullYear(), 0, 1));
    // Summary metrics ignore archived rows so that pre-archive numbers don't
    // shift when a user toggles the show-archived filter at the table level.
    const active = allExpenses.filter(e => !e.archived);

    // Rule #34: canonical money aggregation; Number() at consumer boundary.
    const thisMonth = Number(sumMoney(active
      .filter(e => e.date >= monthStart)
      .map(e => canonicalMoney(e.amount))));

    const ytdDeductibles = Number(sumMoney(active
      .filter(e => e.date >= yearStart)
      .map(e => canonicalMoney(e.amount))));

    const missingReceipts = active
      .filter(e => e.amount > 50 && !e.receipt_url)
      .length;

    return { thisMonth, ytdDeductibles, missingReceipts };
  }, [allExpenses]);

  return { expenses, summary, loading, error, refresh: fetchAll, filters, setFilters };
}
